import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { IconSearch, IconShieldCheck, IconAward, IconLoader2, IconMapPin, IconBadge } from "@tabler/icons-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

/**
 * Public directory of certified Maestras Sombra — anyone (schools, families, HR)
 * can browse the roster and verify authenticity via each teacher's `verification_code`.
 */
export default function SiteMaestrasSombra() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/sello/certified?limit=200");
        setItems(data);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => {
      const hay = [
        x.name,
        ...(x.specialties || []),
        x.bio || "",
        x.city || "",
        x.verification_code || "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleDateString(lang === "es" ? "es-HN" : "en-US", { month: "long", year: "numeric" }) : "";

  return (
    <div>
      <SiteNav />
      <section className="relative overflow-hidden bg-paper">
        <div className="mx-auto max-w-[1180px] px-6 pt-[92px] pb-14">
          <p className="eyebrow">{lang === "es" ? "Directorio profesional" : "Professional directory"}</p>
          <h1 className="font-display font-medium text-[clamp(30px,4.6vw,48px)] leading-[1.05] mt-3">
            {lang === "es" ? "Listado de Maestras Sombra Certificadas" : "Certified Shadow Teachers Directory"}
          </h1>
          <p className="text-muted mt-4 max-w-[52em] text-[14.5px] leading-relaxed">
            {lang === "es"
              ? "Profesionales certificadas por Chiessa Institute tras completar el programa de acompañamiento neurodivergente y superar la entrevista con la Dra. Mariela Chiessa. Cada certificación es verificable con su código único."
              : "Professionals certified by Chiessa Institute after completing the neurodivergent shadow-teacher program and passing a direct interview with Dra. Mariela Chiessa. Each certification is verifiable with a unique code."}
          </p>

          {/* Search */}
          <div className="mt-8 relative max-w-[520px]" data-testid="ms-search">
            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tert pointer-events-none" />
            <input
              type="search"
              className="input pl-9"
              placeholder={lang === "es" ? "Buscar por nombre, especialidad, ciudad o código…" : "Search by name, specialty, city or code…"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="ms-search-input"
            />
          </div>
        </div>
      </section>

      <section className="bg-paper-2 pb-24 pt-4">
        <div className="mx-auto max-w-[1180px] px-6">
          {loading ? (
            <div className="flex items-center gap-2 text-tert py-16">
              <IconLoader2 size={16} className="animate-spin" />
              <span>{lang === "es" ? "Cargando directorio…" : "Loading directory…"}</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="card text-center py-14" data-testid="ms-empty">
              <IconShieldCheck size={28} className="mx-auto text-tert mb-3" />
              <p className="font-display text-[18px] font-medium">
                {items.length === 0
                  ? (lang === "es" ? "Todavía no hay Maestras Sombra certificadas." : "No certified shadow teachers yet.")
                  : (lang === "es" ? "Sin resultados para tu búsqueda." : "No results for your search.")}
              </p>
              <p className="text-tert text-[13px] mt-1">
                {lang === "es" ? "Prueba con otro término (especialidad, ciudad, código)." : "Try another term (specialty, city, code)."}
              </p>
            </div>
          ) : (
            <>
              <p className="text-tert text-[12.5px] mb-4" data-testid="ms-count">
                {filtered.length} {lang === "es" ? "maestras certificadas" : "certified teachers"}
              </p>
              <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="ms-grid">
                {filtered.map((m, idx) => (
                  <motion.li
                    key={m.verification_code || m.name + idx}
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (idx % 12) * 0.04 }}
                    className="card flex flex-col"
                    data-testid={`ms-card-${m.verification_code}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-soft flex items-center justify-center text-teal-deep font-display text-[15px] font-medium shrink-0">
                        {(m.name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-[16.5px] font-medium leading-tight">{m.name}</h3>
                        <div className="flex items-center gap-2 mt-1 text-tert text-[11.5px] flex-wrap">
                          <IconAward size={12} className="text-honey" />
                          <span>{lang === "es" ? "Certificada" : "Certified"} · {fmt(m.issued_at)}</span>
                          {m.city && (
                            <>
                              <span>·</span>
                              <span className="inline-flex items-center gap-1"><IconMapPin size={11} />{m.city}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {m.bio && <p className="text-muted text-[12.5px] mt-3 leading-relaxed line-clamp-3">{m.bio}</p>}
                    {m.specialties?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {m.specialties.slice(0, 5).map((s) => (
                          <span key={s} className="tag text-[10.5px]" style={{ background: "#EEEDFE", color: "#3C3489" }}>{s}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto pt-4 border-t border-line flex items-center justify-between gap-2">
                      <span className="font-mono text-[10.5px] text-tert tracking-wider">{m.verification_code}</span>
                      <Link
                        to={`/verificar/${m.verification_code}`}
                        className="btn-ghost btn text-[11px]"
                        data-testid={`ms-verify-${m.verification_code}`}
                      >
                        <IconBadge size={12} /> {lang === "es" ? "Verificar" : "Verify"}
                      </Link>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
